'use client'

/**
 * KIT D'INTERFACE BLC MÉDIAS (design validé 2026-09-22, inspiration « Baffle »).
 * Toutes les pages l'utilisent pour rester cohérentes :
 *  - fond nuit rgb(var(--sbg)), panneaux rgb(var(--spanel)), bordures blanches à 7 %
 *  - accent violet (rgb(var(--a1)) → rgb(var(--a2)) → rgb(var(--a3))), boutons ronds violets
 *  - titres de page gros et gras, sections en semi-gras + « Voir tout » violet
 * Composants purement visuels : aucune logique métier ici.
 */
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { LucideIcon } from 'lucide-react'
import { ChevronRight, Heart, Play, Pause, Loader2 } from 'lucide-react'
import type { ReactNode, MouseEvent } from 'react'
import Artwork from '@/components/Artwork'
import type { PlayerTrack } from '@/lib/player-context'

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

/* ------------------------------------------------------------------ Page */

/** Conteneur standard d'une page (marges + largeur max). */
export function Page({ children, className = '', wide = false }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={cx('px-4 sm:px-5 md:px-7 pt-4 md:pt-6 pb-10 mx-auto', wide ? 'max-w-[1680px]' : 'max-w-[1400px]', className)}>{children}</div>
}

/** En-tête de page : petite marque, grand titre, sous-titre, actions à droite. */
export function PageHeader({ title, subtitle, icon: Icon, actions, overline = 'BLC MÉDIAS', back }: {
  title: ReactNode; subtitle?: ReactNode; icon?: LucideIcon; actions?: ReactNode; overline?: string | null; back?: ReactNode
}) {
  return (
    <header className="relative isolate mb-6 md:mb-8">
      {/* Aurore vivante (couleurs du thème, respire quand la musique joue). */}
      <div className="fx-aurora" aria-hidden><i /><i /><i /></div>
      {back && <div className="mb-3">{back}</div>}
      {overline && <div className="text-[11px] font-semibold tracking-[0.28em] text-white/55 mb-2">{overline}</div>}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="fx-parallax min-w-0">
          <h1 className="flex items-center gap-3 text-[30px] md:text-[40px] font-extrabold tracking-tight leading-[1.05]">
            {Icon && <Icon size={32} className="text-a3 fill-a3/90 shrink-0" />}
            <span className="truncate">{title}</span>
          </h1>
          {subtitle && <p className="text-sm md:text-[15px] text-white/55 mt-1.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
    </header>
  )
}

/** Section : titre + lien « Voir tout » (ou élément libre à droite). */
export function Section({ title, icon: Icon, href, onSeeAll, right, children, className = '', id }: {
  title: ReactNode; icon?: LucideIcon; href?: string; onSeeAll?: () => void; right?: ReactNode
  children: ReactNode; className?: string; id?: string
}) {
  const seeAll = href
    ? <Link href={href} className="text-[13px] font-medium text-a4 hover:text-white inline-flex items-center gap-0.5 transition">Voir tout <ChevronRight size={15} /></Link>
    : onSeeAll
      ? <button onClick={onSeeAll} className="text-[13px] font-medium text-a4 hover:text-white inline-flex items-center gap-0.5 transition">Voir tout <ChevronRight size={15} /></button>
      : null
  return (
    <section id={id} data-reveal className={cx('mt-10 md:mt-14 first:mt-0 scroll-mt-4', className)}>
      <div className="flex items-end justify-between gap-3 mb-4 md:mb-5">
        <h2 className="sec-title flex items-center gap-2.5 min-w-0">
          {Icon && <Icon className="sec-title-chev text-a3 shrink-0" />}<span className="truncate">{title}</span>
        </h2>
        {right ?? seeAll}
      </div>
      {children}
    </section>
  )
}

/* --------------------------------------------------------------- Surfaces */

/** Panneau / carte de base. */
export function Panel({ children, className = '', as: As = 'div', ...rest }: any) {
  return <As className={cx('lg-surface rounded-2xl bg-spanel/90 border border-white/[0.07]', className)} {...rest}>{children}</As>
}

/** Rangée horizontale défilante (cartes). */
export function Row({ children, className = '' }: { children: ReactNode; className?: string }) {
  // data-dock : la vignette survolée grossit seule, vers le bas (globals.css). pb/-mb : place
  // pour que les vignettes agrandies ne soient pas coupées ; pointer-events-none
  // sur la zone ajoutée pour ne pas bloquer les clics au-dessus (« Voir tout »).
  return <div data-stagger data-reveal data-dock className={cx('relative flex gap-3 md:gap-4 overflow-x-auto pt-2 pb-12 -mb-10 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pointer-events-none [&>*]:pointer-events-auto', className)}>{children}</div>
}

/** Grille responsive standard (2 → 6 colonnes). */
export function Grid({ children, className = '', cols = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' }: { children: ReactNode; className?: string; cols?: string }) {
  return <div data-stagger data-reveal data-hoverzoom className={cx('grid gap-3 md:gap-4', cols, className)}>{children}</div>
}

/* ---------------------------------------------------------------- Boutons */

/** Bouton rond violet « lecture » (comme sur les maquettes). */
export function PlayButton({ playing = false, loading = false, size = 36, onClick, className = '', title = 'Lire' }: {
  playing?: boolean; loading?: boolean; size?: number; onClick?: (e: MouseEvent) => void; className?: string; title?: string
}) {
  const icon = Math.round(size * 0.42)
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title}
      style={{ width: size, height: size }}
      className={cx('lglass-accent shrink-0 rounded-full grid place-items-center text-white bg-gradient-to-br from-a2 to-a1 shadow-[0_6px_18px_rgb(var(--a2)/.45)] hover:brightness-110 active:scale-95 transition', className)}>
      {loading ? <Loader2 size={icon} className="animate-spin" /> : playing ? <Pause size={icon} className="fill-white" /> : <Play size={icon} className="fill-white ml-[2px]" />}
    </button>
  )
}

/** Bouton principal (pilule violette) / secondaire (verre). */
export function Button({ children, variant = 'primary', className = '', icon: Icon, ...rest }: any) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none'
  const v = variant === 'primary'
    ? 'lglass-accent text-white bg-gradient-to-r from-a1 to-a2 shadow-[0_0_22px_rgb(var(--a2)/.45)] hover:brightness-110'
    : variant === 'danger'
      ? 'text-white bg-rose-600/90 hover:bg-rose-600'
      : 'lglass text-white/90 bg-white/[0.07] border border-white/10 hover:bg-white/[0.12]'
  const inner = <>{Icon && <Icon size={16} />}{children}</>
  if (rest.href) { const { href, ...r } = rest; return <Link href={href} className={cx(base, v, className)} {...r}>{inner}</Link> }
  return <button type="button" className={cx(base, v, className)} {...rest}>{inner}</button>
}

/** Bouton icône rond discret (recherche, menu, …). */
export function IconButton({ icon: Icon, className = '', size = 18, ...rest }: any) {
  const cls = cx('lglass w-10 h-10 rounded-full grid place-items-center text-white/80 hover:text-white bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] transition', className)
  if (rest.href) { const { href, ...r } = rest; return <Link href={href} className={cls} {...r}><Icon size={size} /></Link> }
  return <button type="button" className={cls} {...rest}><Icon size={size} /></button>
}

/** Pastille de filtre. */
export function Chip({ active, children, onClick, className = '' }: { active?: boolean; children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={cx('shrink-0 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition',
        active ? 'lglass-accent text-white border-transparent bg-gradient-to-r from-a1 to-a2 shadow-[0_0_14px_rgb(var(--a2)/.5)]'
               : 'lglass text-white/80 border-white/10 bg-white/[0.05] hover:bg-white/[0.1]', className)}>
      {children}
    </button>
  )
}

/** Champ de saisie / recherche. */
export function Input({ icon: Icon, className = '', ...rest }: any) {
  return (
    <div className={cx('relative', className)}>
      {Icon && <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-smuted pointer-events-none" />}
      <input {...rest}
        className={cx('lg-field w-full h-11 rounded-xl bg-white/[0.06] border border-white/[0.09] text-white text-sm placeholder-smuted focus:outline-none focus:border-a2/70 focus:bg-white/[0.08] transition', Icon ? 'pl-11 pr-4' : 'px-4')} />
    </div>
  )
}

/* ------------------------------------------------------------------ Cartes */

/** Palette des tuiles de genre (dégradés profonds, comme les maquettes). */
export const TILE_GRADIENTS = [
  'from-a1 to-adeep', 'from-[#2B4FD8] to-[#1A2A7A]', 'from-[#B0306E] to-[#5A1640]', 'from-[#7A2FD0] to-[#3B1470]',
  'from-[#C2306A] to-[#6A1238]', 'from-[#3B3FD0] to-[#1C1E70]', 'from-[#2E7A4A] to-[#123A24]', 'from-a1 to-adeep',
  'from-[#8A2BE2] to-[#40106E]', 'from-[#D0487A] to-[#6E1C44]', 'from-[#2F5FD0] to-[#15306E]', 'from-[#6A3FD0] to-[#2E1A6E]',
]

/** Tuile de genre : carré arrondi dégradé + icône blanche + libellé. */
export function GenreTile({ label, icon: Icon, emoji, index = 0, href, onClick, className = '', badge }: {
  label: string; icon?: LucideIcon; emoji?: string; index?: number; href?: string; onClick?: () => void; className?: string; badge?: ReactNode
}) {
  const inner = (
    <>
      <span className={cx('absolute inset-0 bg-gradient-to-br', TILE_GRADIENTS[index % TILE_GRADIENTS.length])} />
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,.18),transparent_60%)]" />
      <span className="on-media relative flex flex-col items-center justify-center gap-1.5 h-full">
        {Icon ? <Icon size={30} strokeWidth={1.6} className="text-white drop-shadow" /> : <span className="text-[28px] leading-none">{emoji}</span>}
        <span className="text-[12.5px] font-semibold text-white/95 text-center px-1 leading-tight">{label}</span>
      </span>
      {badge && <span className="absolute top-1.5 right-1.5">{badge}</span>}
    </>
  )
  const tilt = { 'data-tilt': '' }
  const cls = cx('group relative block aspect-[1.25/1] rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-a3/70 hover:-translate-y-0.5 transition', className)
  return href ? <Link href={href} className={cls} {...tilt}>{inner}</Link> : <button type="button" onClick={onClick} className={cx(cls, 'w-full')} {...tilt}>{inner}</button>
}

/** Carte de classement (« 1 · Global Top 50 »). */
export function ChartCard({ rank, title, subtitle, image, href, onClick }: {
  rank?: number | string; title: string; subtitle?: string; image?: string; href?: string; onClick?: () => void
}) {
  const inner = (
    <>
      {image
        ? <img src={image} alt="" referrerPolicy="no-referrer" className="w-12 h-12 rounded-xl object-cover shrink-0" />
        : <span className="w-12 h-12 rounded-xl shrink-0 bg-gradient-to-br from-a1 to-[#B0306E] grid place-items-center text-lg font-extrabold">{rank ?? '♪'}</span>}
      <span className="min-w-0 text-left">
        {image && rank != null && <span className="text-[11px] font-bold text-a4 mr-1">{rank}</span>}
        <span className="block text-[14px] font-semibold truncate">{title}</span>
        {subtitle && <span className="block text-[12px] text-white/50 truncate">{subtitle}</span>}
      </span>
    </>
  )
  const cls = 'shrink-0 w-[220px] flex items-center gap-3 p-3 rounded-2xl bg-spanel border border-white/[0.07] hover:border-a2/50 hover:bg-spanel2 transition'
  return href ? <Link href={href} className={cls}>{inner}</Link> : <button type="button" onClick={onClick} className={cls}>{inner}</button>
}

/** Carte pochette (nouveautés, albums, playlists…) : image + texte en bas. */
export function MediaCard({ image, track, title, subtitle, onClick, href, playing, loading, onPlay, badge, className = '', aspect = 'aspect-square blc-sq', rounded = 'rounded-2xl', fallback }: {
  image?: string;
  /** Titre musical : affiche sa VRAIE pochette (album/single), jamais l'image de la vidéo. */
  track?: PlayerTrack | null; title: string; subtitle?: ReactNode; onClick?: () => void; href?: string; playing?: boolean; loading?: boolean
  onPlay?: () => void; badge?: ReactNode; className?: string; aspect?: string; rounded?: string; fallback?: ReactNode
}) {
  const inner = (
    <>
      <span data-tilt className={cx('lg-film relative block w-full overflow-hidden bg-spanel3 ring-1 ring-white/[0.07]', aspect, rounded)}>
        {track ? <span className="absolute inset-0 transition-transform duration-500 group-hover:scale-105"><Artwork track={track} fill strict size={240} rounded="rounded-none" /></span>
          : image ? <img src={image} alt="" referrerPolicy="no-referrer" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
               : <span className="absolute inset-0 grid place-items-center bg-gradient-to-br from-adeep to-spanel">{fallback}</span>}
        <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        {badge && <span className="absolute top-2 left-2">{badge}</span>}
        <span className="on-media absolute left-3 right-12 bottom-2.5">
          <span className="block text-[15px] md:text-[17px] font-bold leading-tight line-clamp-2 [text-shadow:0_1px_8px_rgba(0,0,0,.6)]">{title}</span>
          {subtitle && <span className="block text-[13px] md:text-sm text-white/70 truncate mt-0.5">{subtitle}</span>}
        </span>
        {onPlay && (
          <span className="absolute right-2 bottom-2" onClick={e => { e.preventDefault(); e.stopPropagation() }}>
            <PlayButton size={34} playing={playing} loading={loading} onClick={() => onPlay()} />
          </span>
        )}
      </span>
    </>
  )
  const cls = cx('group block text-left w-full cursor-pointer', className)
  if (href) return <Link href={href} className={cls}>{inner}</Link>
  // div (et non button) : la carte contient déjà un bouton lecture → pas de bouton imbriqué.
  const act = onClick ?? onPlay
  return <div role="button" tabIndex={0} onClick={act} onKeyDown={e => { if (e.key === 'Enter') act?.() }} className={cls}>{inner}</div>
}

/** Ligne de titre (favoris, listes) : pochette, titre, artiste, cœur, lecture. */
export function TrackRow({ image, track, title, subtitle, index, active, playing, liked, onLike, onPlay, onClick, actions, className = '', menuProps }: {
  image?: string;
  /** Titre musical : VRAIE pochette (album/single). */
  track?: PlayerTrack | null; title: string; subtitle?: ReactNode; index?: number; active?: boolean; playing?: boolean
  liked?: boolean; onLike?: () => void; onPlay?: () => void; onClick?: () => void; actions?: ReactNode; className?: string; menuProps?: any
}) {
  return (
    <div role="button" tabIndex={0} {...menuProps}
      onClick={onClick ?? onPlay}
      onKeyDown={e => { if (e.key === 'Enter') (onClick ?? onPlay)?.() }}
      className={cx('group flex items-center gap-3 md:gap-3.5 p-2 pr-2.5 rounded-2xl border cursor-pointer transition',
        active ? 'bg-spanel2 border-a2/50' : 'bg-spanel/80 border-white/[0.06] hover:bg-spanel2', className)}>
      {index != null && <span className="w-7 text-center text-[16px] md:text-[17px] font-semibold text-white/55 tabular-nums">{index}</span>}
      <span className="relative w-[72px] h-[72px] md:w-20 md:h-20 rounded-xl overflow-hidden shrink-0 bg-spanel3">
        {track ? <Artwork track={track} fill strict size={96} rounded="rounded-xl" />
          : image && <img src={image} alt="" referrerPolicy="no-referrer" loading="lazy" className="w-full h-full object-cover" />}
        {active && playing && (
          <span className="absolute inset-0 bg-adeep/60 flex items-end justify-center gap-0.5 pb-3">
            {[0, 1, 2].map(k => <span key={k} className="w-[3px] h-4 bg-white rounded-sm origin-bottom animate-equalizer" style={{ animationDelay: `${k * 0.2}s` }} />)}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cx('block text-[17px] md:text-[19px] font-semibold truncate', active && 'text-a5')}>{title}</span>
        {subtitle && <span className="block text-[14px] md:text-[15.5px] text-white/60 truncate mt-0.5">{subtitle}</span>}
      </span>
      {actions}
      {onLike && (
        <button type="button" onClick={e => { e.stopPropagation(); onLike() }} title={liked ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          className="w-9 h-9 rounded-full grid place-items-center text-a3 hover:bg-white/[0.06] transition">
          <Heart size={19} className={liked ? 'fill-a3' : ''} />
        </button>
      )}
      {onPlay && <PlayButton size={34} playing={active && playing} onClick={e => { e.stopPropagation(); onPlay() }} />}
    </div>
  )
}

/** Carte large « session live / ambiance » : image, texte, bouton lecture. */
export function WideCard({ image, title, subtitle, onPlay, onClick, href, className = '' }: {
  image?: string; title: string; subtitle?: string; onPlay?: () => void; onClick?: () => void; href?: string; className?: string
}) {
  const inner = (
    <>
      {image ? <img src={image} alt="" referrerPolicy="no-referrer" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
             : <span className="absolute inset-0 bg-gradient-to-br from-adeep via-[#1E1450] to-[#0B0D2A]" />}
      <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <span className="on-media absolute left-3.5 right-14 bottom-3 text-left">
        <span className="block text-[14px] font-bold truncate">{title}</span>
        {subtitle && <span className="block text-[12px] text-white/70 truncate">{subtitle}</span>}
      </span>
      {onPlay && <span className="absolute right-3 bottom-3" onClick={e => { e.preventDefault(); e.stopPropagation() }}><PlayButton size={36} onClick={() => onPlay()} /></span>}
    </>
  )
  const cls = cx('group relative block overflow-hidden rounded-2xl aspect-[16/8] ring-1 ring-white/[0.07]', className)
  if (href) return <Link href={href} className={cls} data-tilt="">{inner}</Link>
  const act = onClick ?? onPlay
  return <div role="button" tabIndex={0} data-tilt="" onClick={act} onKeyDown={e => { if (e.key === 'Enter') act?.() }} className={cx(cls, 'w-full cursor-pointer')}>{inner}</div>
}

/** Carte vidéo 16:9 (même style dans toute la partie vidéo). */
export function VideoTile({ image, title, channel, channelId, live, meta, duration, progress, onClick, href, className = '', children }: {
  image: string; title: string; channel?: string; meta?: string; duration?: string; progress?: number
  /** id de la chaîne → le nom du YouTubeur devient cliquable (page de la chaîne). */
  channelId?: string
  /** vidéo EN DIRECT → pastille « EN DIRECT » rouge */
  live?: boolean
  onClick?: () => void; href?: string; className?: string; children?: ReactNode
}) {
  const router = useRouter()
  const inner = (
    <>
      <span data-tilt className="lg-film relative block aspect-video rounded-2xl overflow-hidden bg-spanel3 ring-1 ring-white/[0.07]">
        <img src={image} alt="" referrerPolicy="no-referrer" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition bg-black/25">
          <span className="w-12 h-12 rounded-full grid place-items-center bg-gradient-to-br from-a2 to-a1 shadow-[0_0_24px_rgb(var(--a2)/.7)]"><Play size={20} className="fill-white ml-0.5" /></span>
        </span>
        {duration && !live && <span className="absolute right-2 bottom-2 text-[11px] font-semibold bg-black/80 px-1.5 py-0.5 rounded-md">{duration}</span>}
        {live && <span className="absolute left-2 top-2 z-[3] inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide bg-[#E5192B] px-2 py-0.5 rounded-md"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />EN DIRECT</span>}
        {progress != null && (
          <span className="absolute left-2 right-2 bottom-2 h-1 rounded-full bg-white/25 overflow-hidden">
            <span className="block h-full bg-gradient-to-r from-a1 to-a3" style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }} />
          </span>
        )}
        {children}
      </span>
      <span className="block mt-2 text-[14.5px] font-semibold leading-snug line-clamp-2">{title}</span>
      {channel && (channelId
        // Nom du YouTubeur CLIQUABLE → page de la chaîne (sans ouvrir la vidéo).
        ? <span role="link" tabIndex={0} title={`Chaîne ${channel}`}
            onClick={e => { e.preventDefault(); e.stopPropagation(); router.push(`/app/video/channel/${channelId}`) }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); router.push(`/app/video/channel/${channelId}`) } }}
            className="block w-fit max-w-full text-[13px] text-white/60 hover:text-a3 hover:underline underline-offset-2 truncate mt-0.5 cursor-pointer">{channel}</span>
        : <span className="block text-[13px] text-white/55 truncate mt-0.5">{channel}</span>)}
      {meta && <span className="block text-[12px] text-white/40 truncate">{meta}</span>}
    </>
  )
  const cls = cx('group block text-left w-full', className)
  if (href) return <Link href={href} className={cls}>{inner}</Link>
  return <button type="button" onClick={onClick} className={cls}>{inner}</button>
}

/* ----------------------------------------------------------------- États */

export function EmptyState({ icon: Icon, title, text, action }: { icon?: LucideIcon; title: string; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-14 px-6 rounded-2xl bg-spanel/70 border border-dashed border-white/10">
      {Icon && <span className="w-14 h-14 rounded-2xl grid place-items-center bg-a2/15 text-a4"><Icon size={26} /></span>}
      <div className="text-lg font-bold">{title}</div>
      {text && <div className="text-sm text-white/55 max-w-md">{text}</div>}
      {action}
    </div>
  )
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={cx('rounded-2xl bg-white/[0.05] animate-pulse', className)} />
}

/** Petit badge (« Nouveau », « E », « PRO »…). */
export function Badge({ children, tone = 'violet' }: { children: ReactNode; tone?: 'violet' | 'pink' | 'green' | 'dark' }) {
  const t = { violet: 'bg-a1', pink: 'bg-[#FF3D9A]', green: 'bg-[#18B979]', dark: 'bg-black/55 backdrop-blur' }[tone]
  return <span className={cx('inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md text-white', t)}>{children}</span>
}

/** Miniature YouTube. Par défaut « mq » : 16:9 SANS bandes noires (hqdefault est
 *  un 4:3 avec bandes noires en haut et en bas). */
export const ytThumb = (id: string, q: 'hq' | 'mq' | 'maxres' | 'hq720' = 'mq') =>
  `https://i.ytimg.com/vi/${id}/${q === 'maxres' ? 'maxresdefault' : q === 'hq720' ? 'hq720' : q + 'default'}.jpg`

/** Id YouTube (11 caractères) extrait d'une URL de miniature ou d'un id. */
function ytIdOf(src: string) {
  const m = /\/vi(?:_webp)?\/([\w-]{11})\//.exec(src || '')
  return m ? m[1] : ''
}

/**
 * Image de pochette qui REMPLIT toujours son cadre (carré ou autre), sans
 * bandes noires : pour une vidéo YouTube on prend « hq720 » (16:9 HD, sans
 * bandes) puis, si elle n'existe pas, « hqdefault » agrandie de 34 % pour
 * rogner ses bandes noires. Recadrage centré (object-cover).
 */
export function CoverImg({ src, videoId, alt = '', className = '', draggable = false }: {
  src?: string; videoId?: string; alt?: string; className?: string; draggable?: boolean
}) {
  const id = videoId && /^[\w-]{11}$/.test(videoId) ? videoId : ytIdOf(src || '')
  const first = id ? `https://i.ytimg.com/vi/${id}/hq720.jpg` : (src || '')
  return (
    <img src={first} alt={alt} draggable={draggable} referrerPolicy="no-referrer" loading="lazy"
      onError={e => {
        const el = e.currentTarget
        if (id && el.src.includes('hq720')) { el.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`; el.style.transform = 'scale(1.34)' }
      }}
      className={cx('absolute inset-0 w-full h-full object-cover pointer-events-none', className)} />
  )
}
